import { useState, useCallback, useRef, useEffect, useMemo } from 'preact/hooks';
import SearchIcon from '../icons/SearchIcon';
import { decodeEntities } from '@wordpress/html-entities';

type SearchInputProps = {
	locations: MapLocation[];
	onSelect: ( originalIndex: number ) => void;
	mapType: string;
};

/**
 * Search input with type-ahead dropdown for searching locations by name.
 * Uses ARIA combobox pattern for accessibility.
 *
 * @param {Object}          props            - Component props.
 * @param {MapLocation[]}   props.locations  - Full array of locations (unfiltered).
 * @param {Function}        props.onSelect   - Callback when a result is selected (receives original index).
 * @return {*} jsx
 */
export default function SearchInput( { locations, onSelect, mapType }: SearchInputProps ) {
	const searchPlaceholder = mapType === 'pif-map' ? 'Search Partner Name' : 'Search Member Name';
	const [ query, setQuery ] = useState( '' );
	const [ debouncedQuery, setDebouncedQuery ] = useState( '' );
	const [ isOpen, setIsOpen ] = useState( false );
	const [ focusedIndex, setFocusedIndex ] = useState( -1 );
	const containerRef = useRef< HTMLDivElement >( null );
	const inputRef = useRef< HTMLInputElement >( null );
	const listboxRef = useRef< HTMLUListElement >( null );

	/**
	 * Debounce the search query (200ms).
	 */
	useEffect( () => {
		const timer = setTimeout( () => setDebouncedQuery( query ), 200 );
		return () => clearTimeout( timer );
	}, [ query ] );

	/**
	 * Filter locations by title match (case-insensitive).
	 * Returns tuples with original index preserved.
	 */
	const results = useMemo( () => {
		const trimmed = debouncedQuery.trim();
		if ( trimmed.length === 0 ) {
			return [];
		}

		const lower = trimmed.toLowerCase();
		return locations
			.map( ( location, index ) => ( { location, originalIndex: index } ) )
			.filter( ( { location } ) => location.title.toLowerCase().includes( lower ) );
	}, [ debouncedQuery, locations ] );

	/**
	 * Show dropdown when the user has typed something.
	 */
	useEffect( () => {
		if ( debouncedQuery.trim().length > 0 ) {
			setIsOpen( true );
			setFocusedIndex( -1 );
		} else {
			setIsOpen( false );
		}
	}, [ debouncedQuery ] );

	/**
	 * Handle input change.
	 */
	const handleInput = useCallback( ( e: Event ) => {
		const value = ( e.target as HTMLInputElement ).value;
		setQuery( value );
	}, [] );

	/**
	 * Select a result — open the dialog and clear the search.
	 */
	const selectResult = useCallback( ( originalIndex: number ) => {
		onSelect( originalIndex );
		setQuery( '' );
		setDebouncedQuery( '' );
		setIsOpen( false );
		setFocusedIndex( -1 );
	}, [ onSelect ] );

	/**
	 * Close dropdown when clicking outside.
	 */
	useEffect( () => {
		if ( ! isOpen ) {
			return;
		}

		const handleClickOutside = ( e: MouseEvent ) => {
			if ( containerRef.current && ! containerRef.current.contains( e.target as Node ) ) {
				setIsOpen( false );
			}
		};

		document.addEventListener( 'mousedown', handleClickOutside );
		return () => document.removeEventListener( 'mousedown', handleClickOutside );
	}, [ isOpen ] );

	/**
	 * Keyboard navigation for the dropdown.
	 */
	const handleKeyDown = useCallback(
		( e: KeyboardEvent ) => {
			if ( ! isOpen ) {
				return;
			}

			switch ( e.key ) {
				case 'ArrowDown':
					e.preventDefault();
					if ( results.length > 0 ) {
						setFocusedIndex( ( prev ) => Math.min( prev + 1, results.length - 1 ) );
					}
					break;
				case 'ArrowUp':
					e.preventDefault();
					if ( results.length > 0 ) {
						setFocusedIndex( ( prev ) => Math.max( prev - 1, 0 ) );
					}
					break;
				case 'Enter':
					e.preventDefault();
					if ( focusedIndex >= 0 && focusedIndex < results.length ) {
						selectResult( results[ focusedIndex ].originalIndex );
					}
					break;
				case 'Escape':
					e.preventDefault();
					setIsOpen( false );
					break;
				case 'Tab':
					setIsOpen( false );
					break;
			}
		},
		[ isOpen, focusedIndex, results, selectResult ]
	);

	/**
	 * Scroll focused option into view.
	 */
	useEffect( () => {
		if ( ! isOpen || focusedIndex < 0 || ! listboxRef.current ) {
			return;
		}

		const focusedEl = listboxRef.current.children[ focusedIndex ] as HTMLElement | undefined;
		focusedEl?.scrollIntoView( { block: 'nearest' } );
	}, [ isOpen, focusedIndex ] );

	const listboxId = 'str-maps-search-listbox';
	const activedescendant = focusedIndex >= 0 ? `str-maps-search-option-${ focusedIndex }` : undefined;

	return (
		<div className="str-maps__search" ref={ containerRef }>
			<label id="str-maps-search-label" className="screen-reader-text">
				{ searchPlaceholder }
			</label>
			<div className="str-maps__search-input-wrapper">
				<input
					ref={ inputRef }
					type="text"
					className="str-maps__search-input small-text-placeholder"
					placeholder={ searchPlaceholder }
					role="combobox"
					aria-expanded={ isOpen }
					aria-haspopup="listbox"
					aria-controls={ isOpen ? listboxId : undefined }
					aria-labelledby="str-maps-search-label"
					aria-activedescendant={ isOpen ? activedescendant : undefined }
					aria-autocomplete="list"
					value={ query }
					onInput={ handleInput }
					onKeyDown={ handleKeyDown }
				/>
				<button
					type="button"
					className="str-maps__search-icon"
					aria-label="Search"
					onClick={ () => {
						if ( results.length > 0 ) {
							selectResult( results[ 0 ].originalIndex );
						}
					} }
				>
					<SearchIcon />
				</button>
			</div>

			{ isOpen && (
				<div className="str-maps__search-dropdown">
					<ul
						id={ listboxId }
						ref={ listboxRef }
						className="str-maps__search-listbox"
						role="listbox"
						aria-labelledby="str-maps-search-label"
					>
						{ results.length > 0 ? (
							results.map( ( { location, originalIndex }, index ) => {
								const isFocused = index === focusedIndex;

								return (
									<li
										key={ originalIndex }
										id={ `str-maps-search-option-${ index }` }
										className={ `str-maps__search-option ${ isFocused ? 'is-focused' : '' }` }
										role="option"
										aria-selected={ isFocused }
										onClick={ () => selectResult( originalIndex ) }
										onMouseEnter={ () => setFocusedIndex( index ) }
									>
										<span className="str-maps__search-option-title">
											{ decodeEntities(location.title) }
										</span>
										<span className="str-maps__search-option-meta">
											{ [ location.city, location.state ].filter( Boolean ).join( ', ' ) }
										</span>
									</li>
								);
							} )
						) : (
							<li
								className="str-maps__search-option str-maps__search-option--empty"
								role="option"
								aria-disabled="true"
								aria-selected={ false }
							>
								No results found
							</li>
						) }
					</ul>
				</div>
			) }
		</div>
	);
}
